import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { fieldDb, sharedDb } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';
import { useScreenWakeLock } from '@/hooks/useScreenWakeLock';
import { extractText } from '@/lib/gemini';
import { formatErr } from '@/utils/format-error';

interface PartnerOption {
  id: string;
  name: string;
  unit_preferences_override: Record<string, string> | null;
}

interface WorkPart {
  id: string;
  name: string;
  display_order: number;
}

interface ExtractionOption {
  id: string;
  building_name_text: string | null;
  order_number: string | null;
  status: string;
  partner_id: string | null;
}

interface MeasurementLine {
  id: string;
  height_cm: number;
  column_count: number;
  cloth_width_m: number;
  area_sqm: number;
  section_index: number;
  display_order: number;
  location_id: string | null;
  room_label: string | null;
}

interface MeasurementRow {
  id: string;
  raw_text: string;
  received_at: string;
  partner_id: string | null;
  extraction_id: string | null;
  line_group_id: string | null;
}

const CLOTH_WIDTH_M = 0.9;

export function ImportPage() {
  const { tenantId } = useAuth();
  const navigate = useNavigate();

  // 採寸紐付け作業中の画面自動ロックを抑制(T13.2・原則 9 補完)
  useScreenWakeLock(true);

  // ペースト取込用(従来機能・縮小)
  const [partners, setPartners] = useState<PartnerOption[]>([]);
  const [partnerId, setPartnerId] = useState<string>('');
  const [rawText, setRawText] = useState('');
  const [pasteBusy, setPasteBusy] = useState(false);
  const [pasteError, setPasteError] = useState<string | null>(null);
  const [pasteOpen, setPasteOpen] = useState(false);

  // 未紐付け採寸一覧
  const [measurements, setMeasurements] = useState<MeasurementRow[]>([]);
  const [linesByMeasurement, setLinesByMeasurement] = useState<Record<string, MeasurementLine[]>>({});
  const [workParts, setWorkParts] = useState<WorkPart[]>([]);
  const [extractions, setExtractions] = useState<ExtractionOption[]>([]);
  const [loading, setLoading] = useState(true);

  const reload = useCallback(async () => {
    if (!tenantId) return;
    setLoading(true);
    const [{ data: meas }, { data: parts }, { data: exs }, { data: parters }] = await Promise.all([
      fieldDb()
        .from('measurements')
        .select('id, raw_text, received_at, partner_id, extraction_id, line_group_id')
        .is('extraction_id', null)
        .order('received_at', { ascending: false })
        .limit(50),
      sharedDb().from('work_parts').select('id, name, display_order').order('display_order'),
      fieldDb()
        .from('extractions')
        .select('id, building_name_text, order_number, status, partner_id')
        .in('status', ['draft', 'confirmed'])
        .order('due_date', { ascending: true, nullsFirst: false })
        .limit(200),
      sharedDb()
        .from('partners')
        .select('id, name, unit_preferences_override')
        .eq('is_active', true)
        .order('name'),
    ]);
    const measRows = (meas ?? []) as MeasurementRow[];
    setMeasurements(measRows);
    setWorkParts((parts ?? []) as WorkPart[]);
    setExtractions((exs ?? []) as ExtractionOption[]);
    setPartners((parters ?? []) as PartnerOption[]);

    if (measRows.length > 0) {
      const { data: lines } = await fieldDb()
        .from('measurement_lines')
        .select('id, measurement_id, height_cm, column_count, cloth_width_m, area_sqm, section_index, display_order, location_id, room_label')
        .in(
          'measurement_id',
          measRows.map((m) => m.id),
        )
        .order('display_order');
      const grouped: Record<string, MeasurementLine[]> = {};
      for (const l of (lines ?? []) as (MeasurementLine & { measurement_id: string })[]) {
        const arr = grouped[l.measurement_id] ?? [];
        arr.push(l);
        grouped[l.measurement_id] = arr;
      }
      setLinesByMeasurement(grouped);
    } else {
      setLinesByMeasurement({});
    }
    setLoading(false);
  }, [tenantId]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const handlePaste = async () => {
    if (!tenantId || !rawText.trim()) return;
    setPasteBusy(true);
    setPasteError(null);
    try {
      const res = await extractText({
        tenantId,
        ...(partnerId ? { partnerId } : {}),
        rawText,
        sourceType: 'paste',
      });
      navigate(`/extractions/${res.extraction_id}`);
    } catch (e) {
      setPasteError(formatErr(e));
    } finally {
      setPasteBusy(false);
    }
  };

  return (
    <div>
      <header className="sticky top-0 z-10 border-b border-slate-200 bg-white/95 px-4 py-3 backdrop-blur">
        <h1 className="text-sm font-semibold">取込</h1>
      </header>

      {/* ============ 未紐付け採寸(Phase 0.8 の核心)============ */}
      <section className="p-4">
        <h2 className="mb-2 text-[10px] font-semibold uppercase tracking-wide text-slate-500">
          未紐付け採寸({measurements.length})
        </h2>
        {loading ? (
          <div className="text-xs text-slate-500">読み込み中...</div>
        ) : measurements.length === 0 ? (
          <div className="rounded-md bg-slate-50 p-4 text-xs text-slate-500">
            LINE グループ宛の採寸テキストはすべて紐付け済みです。
            <br />
            脚立の上から LINE で「550-2 240-2 ...」と送るだけで、自動的にここに溜まります。
          </div>
        ) : (
          <div className="space-y-3">
            {measurements.map((m) => (
              <MeasurementCard
                key={m.id}
                measurement={m}
                lines={linesByMeasurement[m.id] ?? []}
                workParts={workParts}
                extractions={extractions}
                partners={partners}
                onSaved={reload}
              />
            ))}
          </div>
        )}
      </section>

      {/* ============ ペースト取込(従来機能・縮小・必要時のみ展開)============ */}
      <section className="border-t border-slate-100 p-4">
        <button
          type="button"
          onClick={() => setPasteOpen((v) => !v)}
          className="flex w-full items-center justify-between text-[10px] font-semibold uppercase tracking-wide text-slate-500"
        >
          <span>テキスト ペースト取込(SMS / メール本文 等)</span>
          <span className="material-symbols-outlined text-base">{pasteOpen ? 'expand_less' : 'expand_more'}</span>
        </button>
        {pasteOpen && (
          <div className="mt-3 space-y-3">
            <div>
              <label className="mb-1 block text-[10px] font-semibold uppercase tracking-wide text-slate-500">
                取引先(任意)
              </label>
              <select
                value={partnerId}
                onChange={(e) => setPartnerId(e.target.value)}
                className="block w-full rounded-md border border-slate-200 px-3 py-2 text-sm"
              >
                <option value="">未指定</option>
                {partners.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </select>
            </div>
            <textarea
              value={rawText}
              onChange={(e) => setRawText(e.target.value)}
              rows={8}
              placeholder="SMS やメール本文をそのままペースト"
              className="block w-full rounded-md border border-slate-200 px-3 py-2 font-mono text-sm"
            />
            {pasteError && (
              <div className="rounded-md bg-rose-50 p-3 text-xs text-rose-700">{pasteError}</div>
            )}
            <button
              type="button"
              onClick={handlePaste}
              disabled={pasteBusy || !rawText.trim()}
              className="w-full rounded-lg bg-slate-900 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-slate-800 disabled:opacity-50"
            >
              {pasteBusy ? 'AI構造化中...' : 'AI構造化して案件にする'}
            </button>
          </div>
        )}
      </section>

      <section className="border-t border-slate-100 p-4">
        <button
          type="button"
          onClick={() => navigate('/pdf-upload')}
          className="w-full rounded-lg border border-slate-200 px-4 py-3 text-sm font-semibold text-slate-900 hover:bg-slate-50"
        >
          <span className="material-symbols-outlined mr-1 text-base align-middle" aria-hidden="true">
            upload_file
          </span>
          PDFを取り込む(THT・郵送・FAX)
        </button>
      </section>
    </div>
  );
}

// ============================================================
// 採寸 1件(Card)の編集 UI
// ============================================================
function MeasurementCard({
  measurement,
  lines,
  workParts,
  extractions,
  partners,
  onSaved,
}: {
  measurement: MeasurementRow;
  lines: MeasurementLine[];
  workParts: WorkPart[];
  extractions: ExtractionOption[];
  partners: PartnerOption[];
  onSaved: () => void | Promise<void>;
}) {
  // section_index ごとの部位・部屋名(ローカル状態)
  const sections = Array.from(new Set(lines.map((l) => l.section_index))).sort((a, b) => a - b);
  const [sectionState, setSectionState] = useState<
    Record<number, { location_id: string; room_label: string }>
  >(() => {
    const init: Record<number, { location_id: string; room_label: string }> = {};
    for (const s of sections) {
      const firstLine = lines.find((l) => l.section_index === s);
      init[s] = {
        location_id: firstLine?.location_id ?? '',
        room_label: firstLine?.room_label ?? '',
      };
    }
    return init;
  });
  const [extractionId, setExtractionId] = useState(measurement.extraction_id ?? '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const partner = partners.find((p) => p.id === measurement.partner_id);
  const isMUnit = partner?.unit_preferences_override?.['wallpaper_output'] === 'm';

  const handleSave = async () => {
    if (!extractionId) {
      setError('案件を選択してください');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      // section_index ごとの部位・部屋名を measurement_lines に一括反映
      for (const s of sections) {
        const st = sectionState[s];
        if (!st) continue;
        const lineIds = lines.filter((l) => l.section_index === s).map((l) => l.id);
        if (lineIds.length === 0) continue;
        await fieldDb()
          .from('measurement_lines')
          .update({
            location_id: st.location_id || null,
            room_label: st.room_label || null,
          })
          .in('id', lineIds);
      }
      // measurement.extraction_id を更新(紐付け確定)
      await fieldDb()
        .from('measurements')
        .update({ extraction_id: extractionId, promoted_at: new Date().toISOString() })
        .eq('id', measurement.id);
      await onSaved();
    } catch (e) {
      setError(formatErr(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rounded-lg border border-slate-200 bg-white p-3 shadow-sm">
      <div className="mb-2 flex items-baseline justify-between gap-2">
        <div className="text-[10px] text-slate-500">
          {measurement.received_at.slice(0, 16).replace('T', ' ')}
          {partner && <span className="ml-2 font-semibold text-slate-700">{partner.name}</span>}
          {isMUnit && (
            <span className="ml-1 rounded bg-amber-100 px-1 py-0.5 text-[9px] text-amber-800">
              m 単位
            </span>
          )}
        </div>
      </div>

      {/* section ごとの編集 */}
      <div className="space-y-3">
        {sections.map((s) => {
          const sectionLines = lines.filter((l) => l.section_index === s);
          const sectionSum = sectionLines.reduce((a, b) => a + b.area_sqm, 0);
          const sectionDisplay = isMUnit ? sectionSum / CLOTH_WIDTH_M : sectionSum;
          const unitLabel = isMUnit ? 'm' : '㎡';
          const st = sectionState[s];
          return (
            <div key={s} className="rounded-md bg-slate-50 p-2">
              <div className="mb-1 flex items-baseline justify-between gap-2 text-[10px] text-slate-500">
                <span>部屋 {s + 1}</span>
                <span className="font-mono">
                  {sectionLines.length} 行 · {sectionDisplay.toFixed(2)} {unitLabel}
                </span>
              </div>
              <div className="mb-2 font-mono text-[11px] text-slate-700">
                {sectionLines.map((l, i) => (
                  <span key={l.id} className="inline-block">
                    {l.height_cm}
                    {l.column_count > 1 ? `-${l.column_count}` : ''}
                    {i < sectionLines.length - 1 ? ', ' : ''}
                  </span>
                ))}
              </div>
              <div className="grid grid-cols-2 gap-2">
                <select
                  value={st?.location_id ?? ''}
                  onChange={(e) =>
                    setSectionState((prev) => ({
                      ...prev,
                      [s]: { ...prev[s], location_id: e.target.value, room_label: prev[s]?.room_label ?? '' },
                    }))
                  }
                  className="rounded-md border border-slate-200 bg-white px-2 py-1 text-xs"
                >
                  <option value="">部位を選択</option>
                  {workParts.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name}
                    </option>
                  ))}
                </select>
                <input
                  type="text"
                  value={st?.room_label ?? ''}
                  onChange={(e) =>
                    setSectionState((prev) => ({
                      ...prev,
                      [s]: { ...prev[s], room_label: e.target.value, location_id: prev[s]?.location_id ?? '' },
                    }))
                  }
                  placeholder="部屋名(例: 洋室1)"
                  className="rounded-md border border-slate-200 px-2 py-1 text-xs"
                />
              </div>
            </div>
          );
        })}
      </div>

      {/* 案件紐付け */}
      <div className="mt-3 space-y-2">
        <label className="block text-[10px] font-semibold uppercase tracking-wide text-slate-500">
          紐付け先 案件
        </label>
        <select
          value={extractionId}
          onChange={(e) => setExtractionId(e.target.value)}
          className="block w-full rounded-md border border-slate-200 px-3 py-2 text-sm"
        >
          <option value="">案件を選択</option>
          {extractions.map((ex) => (
            <option key={ex.id} value={ex.id}>
              [{ex.status === 'draft' ? '仮' : '確定'}]{' '}
              {ex.building_name_text ?? '(無題)'}
              {ex.order_number ? ` #${ex.order_number}` : ''}
            </option>
          ))}
        </select>
        {error && <div className="rounded-md bg-rose-50 p-2 text-xs text-rose-700">{error}</div>}
        <button
          type="button"
          onClick={handleSave}
          disabled={busy || !extractionId}
          className="w-full rounded-lg bg-emerald-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-emerald-700 disabled:opacity-50"
        >
          {busy ? '保存中...' : '案件に紐付ける'}
        </button>
      </div>
    </div>
  );
}
